import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import FormsTable from '../../components/tables/qhse/forms/FormsTable';
import { usePageTitle } from '../../hooks/usePageTitle';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { useParams } from 'react-router-dom';
import CustomTabs from '../../components/CustomTabs';
import { formTemplateStatusEnum, localStorageKeys, questionTypeEnum } from '../../utils/enums';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';
import { useFormTemplateByIdQuery } from '../../hooks/queries/useFormTemplatesQuery';
import FormEditor from '../../components/forms/FormEditor';
import { Button, Chip, Divider, LinearProgress, Typography } from '@mui/material';
import {
    useCreateFormSectionMutation,
    useDeleteFormSectionMutation,
    useDeleteFormSectionWithContentMutation,
    useDuplicateFormSectionMutation,
    useMoveFormSectionMutation,
    useUpdateFormSectionMutation,
} from '../../hooks/mutations/useFormSectionMutation';
import {
    useCreateFormQuestionMutation,
    useDeleteFormQuestionMutation,
    useDuplicateFormQuestionMutation,
    useMoveFormQuestionMutation,
    useUpdateFormQuestionMutation,
} from '../../hooks/mutations/useFormQuestionMutation';
import { usePublishFormTemplateMutation, useUpdateFormTemplateMutation } from '../../hooks/mutations/useFormTemplateMutation';
import { debounce } from 'lodash';
import { UpdateFormQuestionBody } from '../../services/apiSLTP/qhse/forms/formQuestions';
import { UpdateFormSectionBody } from '../../services/apiSLTP/qhse/forms/formSections';
import { UpdateFormTemplateBody } from '../../services/apiSLTP/qhse/forms/formTemplates';
import {
    useCreateFormQuestionOptionMutation,
    useDeleteFormQuestionOptionMutation,
    useMoveFormQuestionOptionMutation,
    useUpdateFormQuestionOptionMutation,
} from '../../hooks/mutations/useFormQuestionOptionMutation';
import { UpdateFormQuestionOptionBody } from '../../services/apiSLTP/qhse/forms/formQuestionOptions';
import FormSettings from '../../components/forms/FormSettings';
import { useFormAccessByGroupQuery } from '../../hooks/queries/useFormAccessQuery';
import { useCreateFormAccessMutation, useDeleteFormAccessMutation } from '../../hooks/mutations/useFormAccessMutation';
import Icon from '../../components/shared/Icon';
import FormResponses from '../../components/forms/FormResponses';
import PrimaryBox from '../../components/PrimaryBox';

const FormEditPage: React.FC = () => {
    const { t } = useTranslation();
    const { formId } = useParams();

    const [selectedTab, setSelectedTab] = useState(parseInt(getItemFromLocalStorage(localStorageKeys.TAB.FORMEDIT) || '0'));

    const {
        data: formTemplate,
        isLoading: isFormTemplateLoading,
        refetch: refetchFormTemplate,
    } = useFormTemplateByIdQuery({ id: formId || '', queries: { includeFiles: true } });
    const { data: dataFormAccesses, refetch: refetchFormAccesses } = useFormAccessByGroupQuery({ groupId: formTemplate?.groupId || '' });

    const { mutate: createSection, isLoading: isCreatingSectionLoading } = useCreateFormSectionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: moveSection, isLoading: isMovingSectionLoading } = useMoveFormSectionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });

    const { mutate: createQuestion, isLoading: isCreatingQuestionLoading } = useCreateFormQuestionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });

    const { mutate: moveQuestion, isLoading: isMovingQuestionLoading } = useMoveFormQuestionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: publishForm, isLoading: isPublishingFormLoading } = usePublishFormTemplateMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: updateQuestion, isLoading: isUpdatingQuestionLoading } = useUpdateFormQuestionMutation({});
    const { mutate: updateSection, isLoading: isUpdatingSectionLoading } = useUpdateFormSectionMutation({});
    const { mutate: updateTemplate, isLoading: isUpdatingTemplateLoading } = useUpdateFormTemplateMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: duplicateQuestion, isLoading: isDuplicatingQuestionLoading } = useDuplicateFormQuestionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: deleteQuestion, isLoading: isDeletingQuestionLoading } = useDeleteFormQuestionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: duplicateSection, isLoading: isDuplicatingSectionLoading } = useDuplicateFormSectionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: deleteSection, isLoading: isDeletingSectionLoading } = useDeleteFormSectionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: deleteSectionWithContent, isLoading: isDeletingSectionWithContentLoading } = useDeleteFormSectionWithContentMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: createQuestionOption, isLoading: isCreatingQuestionOptionLoading } = useCreateFormQuestionOptionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: deleteQuestionOption, isLoading: isDeletingQuestionOptionLoading } = useDeleteFormQuestionOptionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: updateQuestionOption, isLoading: isUpdatingQuestionOptionLoading } = useUpdateFormQuestionOptionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });
    const { mutate: moveQuestionOption, isLoading: isMovingQuestionOptionLoading } = useMoveFormQuestionOptionMutation({
        onSettled: () => {
            refetchFormTemplate();
        },
    });

    const { mutate: deleteFormAccess, isLoading: isDeletingFormAccessLoading } = useDeleteFormAccessMutation({
        onSettled: () => {
            refetchFormAccesses();
        },
    });
    const { mutate: createFormAccess, isLoading: isCreatingFormAccessLoading } = useCreateFormAccessMutation({
        onSettled: () => {
            refetchFormAccesses();
        },
    });

    usePageTitle(formTemplate?.title || capitalizeFirstLetter(t('forms')));

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
        storeItemToLocalStorage(localStorageKeys.TAB.FORMEDIT, newValue.toString());
    };

    const debouncedUpdateOption = useRef(
        debounce((optionId: string, data: UpdateFormQuestionOptionBody) => {
            updateQuestionOption({ optionId, data });
        }, 500)
    ).current;

    const debouncedUpdateQuestion = useRef(
        debounce((questionId: string, data: UpdateFormQuestionBody) => {
            updateQuestion({ questionId, data });
        }, 500)
    ).current;

    const debouncedUpdateSection = useRef(
        debounce((sectionId: string, data: UpdateFormSectionBody) => {
            updateSection({ sectionId, data });
        }, 500)
    ).current;

    const debouncedUpdateTemplate = useRef(
        debounce((templateId: string, data: UpdateFormTemplateBody, file?: File) => {
            updateTemplate({ templateId, data, file });
        }, 500)
    ).current;

    const formActionsLoading =
        isCreatingSectionLoading ||
        isCreatingQuestionLoading ||
        isMovingQuestionLoading ||
        isPublishingFormLoading ||
        isUpdatingQuestionLoading ||
        isUpdatingSectionLoading ||
        isUpdatingTemplateLoading ||
        isDuplicatingQuestionLoading ||
        isDeletingQuestionLoading ||
        isDuplicatingSectionLoading ||
        isDeletingSectionLoading ||
        isDeletingSectionWithContentLoading ||
        isCreatingQuestionOptionLoading ||
        isDeletingQuestionOptionLoading ||
        isUpdatingQuestionOptionLoading ||
        isMovingQuestionOptionLoading ||
        isMovingSectionLoading ||
        isDeletingFormAccessLoading ||
        isCreatingFormAccessLoading;

    return (
        <PrimaryBox>
            <Header
                title={t('qhse')}
                breadcrumbs={[
                    { label: capitalizeFirstLetter(t('forms')), link: '/qhse/forms' },
                    { label: `${formTemplate?.title || capitalizeFirstLetter(t('form'))}` },
                ]}
                isDateVisible={false}
            >
                {formActionsLoading ? capitalizeFirstLetter(t('saving')) + '...' : capitalizeFirstLetter(t('saved'))}
                <Divider orientation="vertical" flexItem variant="middle" />
                {formTemplate?.status === formTemplateStatusEnum.DRAFT && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => {
                            if (window.confirm(t('areYouSureYouWantToPublishThisForm'))) {
                                publishForm({ templateId: formId || '', withNotification: true });
                            }
                        }}
                    >
                        {capitalizeFirstLetter(t('publish'))}
                    </Button>
                )}
                {formTemplate?.status === formTemplateStatusEnum.PUBLISHED && (
                    <Chip label={t('published').toUpperCase()} color="success" icon={<Icon name="checkCircleOutline" />} sx={{ fontWeight: 600 }} />
                )}
                {formTemplate?.status === formTemplateStatusEnum.INACTIVE && (
                    <Chip label={capitalizeFirstLetter(t('inactive'))} color="info" icon={<Icon name="infoSharp" />} sx={{ fontWeight: 600 }} />
                )}
            </Header>
            <CustomTabs selectedTab={selectedTab} handleTabChange={handleTabChange} tabLabels={[t('theFactory'), t('settings'), t('responses')]} />
            {isFormTemplateLoading && <LinearProgress />}
            {selectedTab === 0 && formTemplate && (
                <>
                    <FormEditor
                        formTemplate={formTemplate}
                        onAddSection={(templateId, order, setSelectedId) =>
                            createSection({ templateId, title: '', order }, { onSuccess: (data) => setSelectedId(data.data._id) })
                        }
                        onMoveSection={(sectionId, order) => moveSection({ sectionId, data: { newOrder: order } })}
                        onAddQuestion={(sectionId, type, order, setSelectedId) =>
                            createQuestion({ sectionId, type, title: '', order }, { onSuccess: (data) => setSelectedId(data.data._id) })
                        }
                        onMoveQuestion={(questionId, toSectionId, order) =>
                            moveQuestion({ questionId, data: { newOrder: order, newSectionId: toSectionId } })
                        }
                        onUpdateQuestion={(questionId, data) => debouncedUpdateQuestion(questionId, data)}
                        onUpdateSection={(sectionId, data) => debouncedUpdateSection(sectionId, data)}
                        onUpdateTemplate={(templateId, data, file) => debouncedUpdateTemplate(templateId, data, file)}
                        onDuplicateQuestion={(questionId, setSelectedId) =>
                            duplicateQuestion({ questionId }, { onSuccess: (data) => setSelectedId(data.data._id) })
                        }
                        onDeleteQuestion={(questionId) => deleteQuestion({ questionId })}
                        onDuplicateSection={(sectionId, setSelectedId) =>
                            duplicateSection({ sectionId }, { onSuccess: (data) => setSelectedId(data.data._id) })
                        }
                        onDeleteSection={(sectionId, withContent) =>
                            withContent ? deleteSectionWithContent({ sectionId }) : deleteSection({ sectionId })
                        }
                        onAddQuestionOption={(data) => createQuestionOption({ ...data }, { onSuccess: () => {} })}
                        onDeleteQuestionOption={(optionId) => deleteQuestionOption({ optionId })}
                        onUpdateQuestionOption={(optionId, data) => debouncedUpdateOption(optionId, data)}
                        onMoveQuestionOption={(optionId, data) => moveQuestionOption({ optionId, data })}
                        disabled={formTemplate.status !== formTemplateStatusEnum.DRAFT}
                    />
                </>
            )}
            {selectedTab === 1 && formTemplate && (
                <>
                    <FormSettings
                        formTemplate={formTemplate}
                        onUpdateTemplate={debouncedUpdateTemplate}
                        disabled={formTemplate.status !== formTemplateStatusEnum.DRAFT}
                        formAccesses={dataFormAccesses}
                        onCreateFormAccess={(data) => createFormAccess({ ...data })}
                        onDeleteFormAccess={(formAccessId) => deleteFormAccess({ formAccessId })}
                    />
                </>
            )}
            {selectedTab === 2 && formTemplate && <FormResponses formTemplate={formTemplate} />}
        </PrimaryBox>
    );
};

export default FormEditPage;
